import {InputSelect} from '@/components/commons/input-select/InputSelect'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {useTranslation} from '@/translations/i18n'
import {Controller, useFormContext, useWatch} from 'react-hook-form'
import {userStatusMapToColor, userInterruptionReasonToLabel, userStatusToLabel} from '@/features/users/utils'
import {InputDate} from '@/components/commons/input-date-legacy/InputDate'
import dayjs from 'dayjs'
import {UserOperatorForm} from '@/features/users/components/user-operator-form/UserOperatorForm'
import {UserWorkAreasForm} from '@/features/users/components/user-work-areas-form/UserWorkAreasForm'
import {pickPropsFromObj} from '@/utilities/helpers'
import {UserExtended, UserJobInfoFormValidationSchema} from '@/features/users/types'
import {SquareIcon} from '@/components/ui/icon/Icon'
import {RadioField} from '@/components/commons/radio-field/RadioField'
import {Label} from '@/components/ui/label/Label'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {InputText} from '@/components/commons/input-text/InputText'
import {useState} from 'react'

export const UserJobInfoForm = ({
    includesAllStatuses,
    isEditMode
}: {
    includesAllStatuses?: boolean
    isEditMode?: boolean
}) => {
    const {t} = useTranslation()

    // methods
    const form = useFormContext<UserJobInfoFormValidationSchema>()

    // form observer
    const status = useWatch({name: 'status', control: form.control})
    const specialization = useWatch({name: 'specialization', control: form.control})
    const suspensionStartDate = useWatch({name: 'suspensionStartDate', control: form.control})
    const suspensionEndDate = useWatch({name: 'suspensionEndDate', control: form.control})
    const suspensionReason = useWatch({name: 'suspensionReason', control: form.control})
    const temporaryAbsenceEndDate = useWatch({name: 'temporaryAbsenceEndDate', control: form.control})
    const temporaryAbsenceStartDate = useWatch({name: 'temporaryAbsenceStartDate', control: form.control})
    const startDate = useWatch({name: 'startDate', control: form.control})

    // state
    const [customSuspensionReason, setCustomSuspensionReason] = useState(
        () =>
            status == 'suspended' && suspensionReason != t('users:maternity') && suspensionReason != t('users:disease')
    )

    // side effects
    const onChangeStatusSideEffects = () => {
        if (status == 'candidate') {
            form.setValue('startDate', null, {shouldDirty: true})
            form.setValue('candidatureDate', null, {shouldDirty: true})
            form.setValue('contractType', null, {shouldDirty: true})
            form.setValue('vatNumber', null, {shouldDirty: true})
            form.setValue('weeklyHours', null, {shouldDirty: true})
            form.setValue('amount', null, {shouldDirty: true})
            form.setValue('raExempt', false, {shouldDirty: true})
            form.setValue('paymentMethod', null, {shouldDirty: true})
            form.setValue('iban', null, {shouldDirty: true})
            form.setValue('workSuitableUpTo', null, {shouldDirty: true})
            form.setValue('role', null, {shouldDirty: true})
            form.setValue('permissionsOverwritten', false, {shouldDirty: true})
            form.setValue('privacyB', false, {shouldDirty: true})
            form.setValue('privacyC', false, {shouldDirty: true})
            form.setValue('fm68', false, {shouldDirty: true})
            form.setValue('privacyYear', null, {shouldDirty: true})
        }

        form.setValue('suspensionStartDate', null, {shouldDirty: true})
        form.setValue('suspensionEndDate', null, {shouldDirty: true})
        form.setValue('suspensionReason', null, {shouldDirty: true})
        form.setValue('temporaryAbsenceStartDate', null, {shouldDirty: true})
        form.setValue('temporaryAbsenceEndDate', null, {shouldDirty: true})
        form.setValue('interruptionDate', null, {shouldDirty: true})
        form.setValue('interruptionReason', null, {shouldDirty: true})
        form.clearErrors([
            'startDate',
            'candidatureDate',
            'contractType',
            'vatNumber',
            'paymentMethod',
            'iban',
            'workSuitableUpTo',
            'suspensionReason',
            'suspensionEndDate',
            'suspensionReason',
            'temporaryAbsenceStartDate',
            'temporaryAbsenceEndDate',
            'interruptionDate',
            'interruptionReason'
        ])
    }

    const statusOptions = includesAllStatuses
        ? userStatusToLabel
        : (pickPropsFromObj(userStatusToLabel, ['candidate', 'operative']) as typeof userStatusToLabel)

    return (
        <Grid columns={6} fullWidth gap={4}>
            <GridItem startColumn={1} endColumn={4}>
                <Controller
                    control={form.control}
                    name="status"
                    render={({field}) => (
                        <InputSelect
                            label={t('commons:status')}
                            optionsMap={statusOptions}
                            sameWidth={true}
                            errorMessage={form.formState.errors.status?.message}
                            value={field.value}
                            onChange={value => {
                                field.onChange(value)
                                onChangeStatusSideEffects()
                            }}
                            displayValue={label => (
                                <Flexbox align="center" gap={1}>
                                    <SquareIcon
                                        className="custom"
                                        size={8}
                                        color={userStatusMapToColor[field.value as UserExtended['status1']]}
                                    />
                                    {label}
                                </Flexbox>
                            )}
                        />
                    )}
                />
            </GridItem>

            {status != 'candidate' && (
                <GridItem startColumn={4} endColumn={7}>
                    <Controller
                        name="startDate"
                        control={form.control}
                        render={({field, fieldState}) => (
                            <InputDate
                                label={t('users:start_date')}
                                placeholder="GG/MM/AAAA"
                                errorMessage={fieldState.error?.message}
                                maxDate={dayjs().toDate()}
                                numMonths={1}
                                fullWidth
                                selectedDates={field.value ? [field.value] : []}
                                onChange={field.onChange}
                                disabled={!!field.value && isEditMode}
                            />
                        )}
                    />
                </GridItem>
            )}

            {status == 'candidate' && (
                <GridItem startColumn={4} endColumn={7}>
                    <Controller
                        name="candidatureDate"
                        control={form.control}
                        render={({field, fieldState}) => (
                            <InputDate
                                label={t('users:application_date')}
                                placeholder="GG/MM/AAAA"
                                errorMessage={fieldState.error?.message}
                                maxDate={dayjs().toDate()}
                                numMonths={1}
                                fullWidth
                                selectedDates={field.value ? [field.value] : []}
                                onChange={field.onChange}
                            />
                        )}
                    />
                </GridItem>
            )}

            {status == 'suspended' && (
                <>
                    <GridItem spanColumn={3}>
                        <Controller
                            name="suspensionStartDate"
                            control={form.control}
                            render={({field, fieldState}) => (
                                <InputDate
                                    label={t('users:suspension_start_date')}
                                    placeholder="GG/MM/AAAA"
                                    errorMessage={fieldState.error?.message}
                                    maxDate={suspensionEndDate ? dayjs(suspensionEndDate).toDate() : undefined}
                                    numMonths={1}
                                    fullWidth
                                    selectedDates={field.value ? [field.value] : []}
                                    onChange={field.onChange}
                                />
                            )}
                        />
                    </GridItem>
                    <GridItem spanColumn={3}>
                        <Controller
                            name="suspensionEndDate"
                            control={form.control}
                            render={({field, fieldState}) => (
                                <InputDate
                                    label={t('users:suspension_end_date')}
                                    placeholder="GG/MM/AAAA"
                                    errorMessage={fieldState.error?.message}
                                    minDate={suspensionStartDate ? dayjs(suspensionStartDate).toDate() : undefined}
                                    numMonths={1}
                                    fullWidth
                                    selectedDates={field.value ? [field.value] : []}
                                    onChange={field.onChange}
                                />
                            )}
                        />
                    </GridItem>
                    <GridItem spanColumn={6} direction="column" gap={2.5}>
                        <Flexbox direction="column" gap={1.5}>
                            <Label>{t('commons:reason')}</Label>
                            <Flexbox gap={4}>
                                <Controller
                                    name="suspensionReason"
                                    render={({field}) => (
                                        <RadioField
                                            name="suspensionReason"
                                            label={t('users:disease')}
                                            value={t('users:disease')}
                                            checked={suspensionReason == t('users:disease')}
                                            onChange={event => {
                                                field.onChange(event.currentTarget.value)
                                                setCustomSuspensionReason(false)
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name="suspensionReason"
                                    render={({field}) => (
                                        <RadioField
                                            name="suspensionReason"
                                            label={t('users:maternity')}
                                            value={t('users:maternity')}
                                            checked={suspensionReason == t('users:maternity')}
                                            onChange={event => {
                                                field.onChange(event.currentTarget.value)
                                                setCustomSuspensionReason(false)
                                            }}
                                        />
                                    )}
                                />

                                <RadioField
                                    name="suspensionReason"
                                    label={t('commons:other')}
                                    value={t('commons:other')}
                                    checked={customSuspensionReason}
                                    onChange={() => {
                                        form.setValue('suspensionReason', null, {shouldDirty: true})
                                        setCustomSuspensionReason(prev => !prev)
                                    }}
                                />
                            </Flexbox>
                            {form.formState.errors.suspensionReason?.message && !customSuspensionReason && (
                                <InputHelpText error={form.formState.errors.suspensionReason.message} />
                            )}
                        </Flexbox>
                        {customSuspensionReason && (
                            <InputText
                                type="text"
                                label={t('users:description')}
                                placeholder={t('users:suspension_reason_placeholder')}
                                errorMessage={form.formState.errors.suspensionReason?.message}
                                defaultValue={suspensionReason ?? ''}
                                {...form.register('suspensionReason')}
                            />
                        )}
                    </GridItem>
                </>
            )}

            {status == 'absent' && (
                <>
                    <GridItem spanColumn={3}>
                        <Controller
                            name="temporaryAbsenceStartDate"
                            control={form.control}
                            render={({field, fieldState}) => (
                                <InputDate
                                    label={t('users:temporary_absence_start_date')}
                                    placeholder="GG/MM/AAAA"
                                    errorMessage={fieldState.error?.message}
                                    maxDate={
                                        temporaryAbsenceEndDate ? dayjs(temporaryAbsenceEndDate).toDate() : undefined
                                    }
                                    numMonths={1}
                                    fullWidth
                                    selectedDates={field.value ? [field.value] : []}
                                    onChange={field.onChange}
                                />
                            )}
                        />
                    </GridItem>
                    <GridItem spanColumn={3}>
                        <Controller
                            name="temporaryAbsenceEndDate"
                            control={form.control}
                            render={({field, fieldState}) => (
                                <InputDate
                                    label={t('users:temporary_absence_end_date')}
                                    placeholder="GG/MM/AAAA"
                                    errorMessage={fieldState.error?.message}
                                    minDate={
                                        temporaryAbsenceStartDate
                                            ? dayjs(temporaryAbsenceStartDate).toDate()
                                            : undefined
                                    }
                                    numMonths={1}
                                    fullWidth
                                    selectedDates={field.value ? [field.value] : []}
                                    onChange={field.onChange}
                                />
                            )}
                        />
                    </GridItem>
                </>
            )}

            {status == 'terminated' && (
                <>
                    <GridItem spanColumn={3}>
                        <Controller
                            name="interruptionDate"
                            control={form.control}
                            render={({field, fieldState}) => (
                                <InputDate
                                    label={t('users:interruption_date')}
                                    placeholder="GG/MM/AAAA"
                                    errorMessage={fieldState.error?.message}
                                    minDate={startDate ? dayjs(startDate).toDate() : undefined}
                                    numMonths={1}
                                    fullWidth
                                    selectedDates={field.value ? [field.value] : []}
                                    onChange={field.onChange}
                                    disabled={!!field.value && isEditMode}
                                />
                            )}
                        />
                    </GridItem>
                    <GridItem spanColumn={3}>
                        <Controller
                            control={form.control}
                            name="interruptionReason"
                            render={({field, fieldState}) => (
                                <InputSelect
                                    label={t('users:interruption_reason')}
                                    optionsMap={userInterruptionReasonToLabel}
                                    disabled={!specialization}
                                    sameWidth
                                    value={field.value ?? undefined}
                                    onChange={field.onChange}
                                    errorMessage={fieldState.error?.message}
                                />
                            )}
                        />
                    </GridItem>
                </>
            )}

            <UserOperatorForm />

            {specialization && specialization?.name != 'Amministrativo' && <UserWorkAreasForm />}
        </Grid>
    )
}
